import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { clearToken, getToken, setToken, setUnauthorizedHandler } from '../api/api';
import { login as loginRequest, microsoftToken, rememberUsername } from '../api/core';
import { toast } from '../lib/toastBus';
import { clearRedirectGuard, markExplicitLogout } from './entraRedirectGuard';

interface AuthContextValue {
  token: string | null;
  isAuthenticated: boolean;
  /** `username` er paakraevet og bliver JWT'ens `sub` (kontrakt 0.11.0). */
  login: (username: string, password: string) => Promise<void>;
  /**
   * Veksler Microsoft-loginets handoff-cookie til en JWT (POST /api/auth/microsoft/token)
   * og gemmer den som ved almindeligt login (kontrakt 0.21.0).
   */
  loginWithMicrosoft: () => Promise<void>;
  logout: (message?: string) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setTokenState] = useState<string | null>(() => getToken());

  // Bevidst "Log ud": loginsiden må ikke straks sende brugeren videre til Microsoft igen
  // (0.21.1). Session-udløb går via 401-handleren nedenfor og sætter IKKE flaget.
  const logout = useCallback((message?: string) => {
    markExplicitLogout();
    clearToken();
    setTokenState(null);
    if (message) toast.info(message);
  }, []);

  // Med et token er login lykkedes: Microsoft-løkkeværnet nulstilles (også ved ?ms=ok).
  useEffect(() => {
    if (token) clearRedirectGuard();
  }, [token]);

  // api.ts kalder denne, naar et kald svarer 401.
  useEffect(() => {
    setUnauthorizedHandler(() => {
      setTokenState((current) => {
        if (current) toast.warning('Din session er udløbet. Log ind igen.');
        return null;
      });
    });
    return () => setUnauthorizedHandler(null);
  }, []);

  // Log ud i alle faner, hvis token fjernes i én af dem.
  useEffect(() => {
    function onStorage(event: StorageEvent) {
      if (event.key === 'hbc_token') setTokenState(event.newValue);
    }
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const login = useCallback(async (username: string, password: string) => {
    const response = await loginRequest(username, password);
    if (!response?.token) {
      throw new Error('Serveren returnerede ingen token.');
    }
    // Brugernavnet huskes foerst, naar loginnet lykkedes.
    rememberUsername(username);
    setToken(response.token);
    setTokenState(response.token);
  }, []);

  const loginWithMicrosoft = useCallback(async () => {
    const response = await microsoftToken();
    if (!response?.token) {
      throw new Error('Serveren returnerede ingen token.');
    }
    // Ingen rememberUsername: brugeren har intet brugernavn i dette flow.
    setToken(response.token);
    setTokenState(response.token);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ token, isAuthenticated: Boolean(token), login, loginWithMicrosoft, logout }),
    [token, login, loginWithMicrosoft, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth skal bruges inden i en AuthProvider.');
  return context;
}
